import { Button } from "@mui/material";
import { useEffect } from "react";
import MuteButton from "../MuteButton";
import { PhaseRingSegments } from "./PhaseRingSegments";
import { convertSecondsToDurationString } from "../../utils/time";
import { UpcomingSessionInfo } from "./UpcomingSessionInfo";
import { DEFAULT_DOOR_IMAGE, RoomParams } from "../../pages/Room";
import { useTheme } from "@emotion/react";
import { getThemeColor } from "../../utils/color";
import { useMuted } from "../../audio/mutedContext";
import { useConfig } from "../../config/configContext";


export const ClockLayoutRoomDisplay = (params: RoomParams) => {
    const {
        room,
        inside,
        isPlayingAudio,
        currentSessionInfo,
        currentPhase,
        upcomingPhases,
        pauseImage,
        leaveRoom,
        enterRoom,
        joinZoom,
    } = params;
    const {
        currentSession,
        upcomingSession,
        upcomingSessionIsTomorrow,
        currentSessionRunningTime,
        timeUntilUpcomingSession,
    } = currentSessionInfo; 

    const config = useConfig();

    useEffect(() => {
        //if room.headless, add "overflow: hidden" to html 
        if (room.headless) {
            document.documentElement.style.overflow = "hidden";
        } else {
            document.documentElement.style.overflow = "";
        }
        return () => {
            //remove "overflow: hidden" from html
            document.documentElement.style.overflow = "";
        }
    }, [room.headless]);

    const [muted] = useMuted();

    const theme = useTheme() as any;
    const roomColor = getThemeColor(theme, room.color);

    const backgroundImage = inside
        ? currentSession
            ? currentSession.graphic.url
            : //random pause image 
            pauseImage
        : (room.doorImage || DEFAULT_DOOR_IMAGE);
    const backgroundImageHover = inside ? undefined : room.doorImageHover;
    const exitDorImage = room.doorImageHover || room.doorImage || DEFAULT_DOOR_IMAGE;

    return (
        <div
            className={"responsive room " +
                (inside ? "inside" : "outside") +
                (isPlayingAudio && inside ? " is-playing-audio" : "") +
                (currentSession ? " session" : " pause") +
                (room.headless ? " headless" : "")
            }
            id={"room-" + room.name}
        >
            {!room.headless &&
                <div className="room-head">
                    {inside ?
                        <Button
                            key="leave-room"
                            variant='outlined'
                            color={room.color as any}
                            startIcon={<div style={{ backgroundImage: `url(${exitDorImage})` }} />}
                            size='large'
                            fullWidth={true}
                            onClick={leaveRoom}
                            sx={{
                            }}
                        >
                            ◀ &nbsp; Verlassen
                        </Button>
                        :
                        <Button
                            key="enter-room"
                            variant='contained'
                            color={room.color as any}
                            size='large'
                            fullWidth={true}
                            onClick={enterRoom}
                            sx={{
                            }}
                        >
                            Beitreten &nbsp; ▶
                        </Button>
                    }
                </div>
            }

            <div className="room-image-container">
                <div className="layer img door"
                    style={{ backgroundImage: `url(${backgroundImage})`, cursor: inside ? "default" : "pointer" }}
                    title={inside ? undefined : "Beitreten   ▶"}
                    onClick={enterRoom}
                >
                </div>
                {backgroundImageHover &&
                    <div className="layer img door-open"
                        style={{ backgroundImage: `url(${backgroundImageHover})`, cursor: "pointer" }}
                        title={inside ? undefined : "Beitreten   ▶"}
                        onClick={enterRoom}
                    >
                    </div>
                }
                {inside && isPlayingAudio && currentSession?.graphic.urlWithVolume &&
                    <div className="layer img only-show-if-audio-is-playing"
                        style={{ backgroundImage: `url(${currentSession.graphic.urlWithVolume})` }}
                    >
                    </div>
                }
                {inside && currentSession &&
                    <PhaseRingSegments passedTime={currentSessionRunningTime} upcomingPhases={upcomingPhases} graphic={currentSession.graphic}
                    />
                }
                {inside && currentSession && currentPhase && (room.countdownPosition === "center") &&
                    <div className="layer center">
                        <div className="text-bold center-circle">
                            {convertSecondsToDurationString(currentPhase.remainingTime ?? currentPhase.duration)}
                        </div>
                    </div>
                }
            </div>

            <div className="room-info">
                {inside
                    ? //inside 
                    <>
                        {currentSession
                            ? // display current session name, current phase, time left of current phase (MM:SS)
                            <div className="text-center">
                                <p>
                                    <span style={{ color: roomColor }}>{currentSession.name}</span>
                                </p>
                                {currentPhase &&
                                    <>
                                        <p>
                                            <span className="text-bold" style={{ color: roomColor }}>{currentPhase.name}</span>
                                            <span style={{ position: "relative", display: "inline-block", top: -1 }}>
                                                <MuteButton isPlayingAudio={!!(inside && isPlayingAudio)} color={room.color} />
                                            </span>
                                            {(room.countdownPosition ?? "bottom") === "bottom" && (
                                                <>
                                                    <br />
                                                    noch <span className="text-bold">{convertSecondsToDurationString(currentPhase.remainingTime ?? currentPhase.duration)}</span>
                                                </>
                                            )}
                                        </p>
                                        {
                                            currentPhase.description &&
                                            <p>
                                                {currentPhase.description}
                                            </p>
                                        }
                                    </>
                                }
                            </div>
                            : // display upcoming session: name, start time of day (local time! local time format!)
                            <div className="text-center">
                                <UpcomingSessionInfo
                                    timeUntilUpcomingSession={timeUntilUpcomingSession}
                                    upcomingSession={upcomingSession}
                                    upcomingSessionIsTomorrow={upcomingSessionIsTomorrow}
                                    roomColor={roomColor} />

                                <p className="with-border" style={{ borderColor: roomColor }}>
                                    {/* 🔊 Bitte lasse die Lautstärke<br />Deines PCs hochgedreht:<br /> */}
                                    <span style={{ color: roomColor }}>
                                        Der <b>Gong</b> <MuteButton isPlayingAudio={false} color={room.color} /><br />
                                        {muted
                                            ? <>
                                                wird Dich <b>nicht</b> zur Session rufen,<br />
                                                da Du den Ton stummgeschaltet hast!
                                            </>
                                            : <>
                                                wird Dich zur Session rufen.<br />
                                                Bitte lass dieses Fenster dafür offen und sichtbar.
                                            </>
                                        }
                                    </span>
                                </p>
                            </div>
                        }
                        {room.zoomLink &&
                            (currentSession ||
                                (!!config.zoom.buttonVisible_beforeSessionStart && config.zoom.buttonVisible_beforeSessionStart >= timeUntilUpcomingSession))
                            && //maybe display zoom link 
                            <div className="room-info-button">
                                <Button
                                    endIcon={<div style={{ backgroundImage: "url(/img/zoom-logo.png)" }} />}
                                    variant='contained'
                                    color={room.color as any}
                                    size='large'
                                    fullWidth={true}
                                    onClick={joinZoom}
                                    sx={{
                                    }}
                                >
                                    In Zoom beitreten
                                </Button>
                            </div>
                        }
                    </>
                    : //outside 
                    <>
                        {currentSession
                            ? // display current session name, and time that is has been running for
                            <div className="text-center">
                                <p>
                                    <span className="text-bold" style={{ color: roomColor }}>{currentSession.name}</span><br />
                                    läuft seit <span className="text-bold">{convertSecondsToDurationString(currentSessionRunningTime)}</span>.
                                </p>
                            </div>
                            : // display upcoming session: name, start time of day (local time! local time format!)
                            <div className="text-center">
                                <UpcomingSessionInfo
                                    timeUntilUpcomingSession={timeUntilUpcomingSession}
                                    upcomingSession={upcomingSession}
                                    upcomingSessionIsTomorrow={upcomingSessionIsTomorrow}
                                    roomColor={roomColor} />
                            </div>
                        }
                    </>
                }

            </div>
        </div>
    );
}

export default ClockLayoutRoomDisplay;